import type { Key, Selection } from "@react-types/shared";
import { useCallback, useMemo, useState, type FC } from "react";

import { Button } from "@/components/flexkit/Button";
import { DataTable, RowActionsItem, type ColumnDef } from "@/components/flexkit/tables/DataTable";
import type { MobileRowDescriptor } from "@/components/flexkit/tables/DataTableMobileView";
import { useToast } from "@/components/flexkit/toast";
import { Text } from "@/components/flexkit/typography/Text";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { DeleteSecretModal } from "@/components/secrets/DeleteSecretModal";
import { EditSecretModal } from "@/components/secrets/EditSecretModal";
import { lastUpdatedDate, mountTypeLabel } from "@/components/secrets/secret-utils";
import { timeAgo } from "@/format/time";
import { TrackLocations } from "@/hooks/use-segment";
import { useMemberQuery, usePrefetchMembersByIds } from "@/queries/organization-queries";
import { useDeleteSecret, type PlainSecret } from "@/queries/secret-queries";
import { formatError } from "@/utils/errors";
import { pluralize } from "@/utils/strings";

type Props = {
    secrets: PlainSecret[];
    showCreatorColumn?: boolean;
    canUpdateSecret?: boolean;
    canDeleteSecret?: boolean;
};

export const SecretsTable: FC<Props> = ({
    secrets,
    showCreatorColumn = true,
    canUpdateSecret = true,
    canDeleteSecret = true,
}) => {
    const [secretToDelete, setSecretToDelete] = useState<PlainSecret | null>(null);
    const [secretToEdit, setSecretToEdit] = useState<PlainSecret | null>(null);
    const [bulkDeleteSecrets, setBulkDeleteSecrets] = useState<PlainSecret[] | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

    const creatorIds = useMemo(() => secrets.map((s) => s.creator?.id).filter((id): id is string => !!id), [secrets]);
    usePrefetchMembersByIds(creatorIds, { enabled: showCreatorColumn });

    const sorted = useMemo(() => {
        return [...secrets].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    }, [secrets]);

    // ── Column definitions ──────────────────────────────────────────

    const columns = useMemo<ColumnDef<PlainSecret>[]>(() => {
        const cols: ColumnDef<PlainSecret>[] = [
            {
                id: "name",
                header: "Name",
                isRowHeader: true,
                cell: (secret) => (
                    <Text className="truncate font-mono text-base font-bold text-content-primary">{secret.name}</Text>
                ),
            },
            {
                id: "type",
                header: "Type",
                className: "hidden @md:table-cell",
                cell: (secret) => <Text className="truncate text-base">{mountTypeLabel(secret)}</Text>,
            },
        ];

        if (showCreatorColumn) {
            cols.push({
                id: "createdBy",
                header: "Created by",
                className: "hidden @lg:table-cell",
                cell: (secret) => <CreatedByCell creatorId={secret.creator?.id} />,
            });
        }

        cols.push({
            id: "lastUpdated",
            header: "Last updated",
            className: "hidden @md:table-cell",
            cell: (secret) => {
                const date = lastUpdatedDate(secret);
                return date ? (
                    <Text className="truncate text-base" title={date.toISOString()}>
                        {timeAgo(date)}
                    </Text>
                ) : (
                    <span className="text-content-secondary">--</span>
                );
            },
        });

        return cols;
    }, [showCreatorColumn]);

    // ── Row actions ─────────────────────────────────────────────────

    const rowActions = useCallback(
        (secret: PlainSecret) => (
            <>
                {canUpdateSecret && (
                    <RowActionsItem
                        onClick={() => setSecretToEdit(secret)}
                        data-testid={`edit-secret-${secret.id}-button`}
                        data-tracking-id="edit-secret-secrets-table"
                    >
                        Edit
                    </RowActionsItem>
                )}
                {canDeleteSecret && (
                    <RowActionsItem
                        onClick={() => setSecretToDelete(secret)}
                        className="text-content-red"
                        data-testid={`delete-secret-${secret.id}-button`}
                        data-tracking-id="delete-secret-secrets-table"
                    >
                        Delete
                    </RowActionsItem>
                )}
            </>
        ),
        [canUpdateSecret, canDeleteSecret],
    );

    const hasRowActions = canUpdateSecret || canDeleteSecret;

    // ── Mobile row ──────────────────────────────────────────────────

    const mobileRow = useCallback(
        (secret: PlainSecret): MobileRowDescriptor => ({
            title: <span className="truncate font-mono font-bold text-content-primary">{secret.name}</span>,
            details: [
                { label: "Type", value: mountTypeLabel(secret) },
                ...(showCreatorColumn
                    ? [
                          {
                              label: "Created by",
                              value: <CreatedByName creatorId={secret.creator?.id} />,
                          },
                      ]
                    : []),
                {
                    label: "Last updated",
                    value: (() => {
                        const date = lastUpdatedDate(secret);
                        return date ? timeAgo(date) : "--";
                    })(),
                },
            ],
        }),
        [showCreatorColumn],
    );

    // ── Bulk delete ─────────────────────────────────────────────────

    const handleBulkDelete = useCallback(() => {
        const selectedSet = selectedKeys === "all" ? new Set(sorted.map((s) => s.id)) : (selectedKeys as Set<Key>);
        const toDelete = sorted.filter((s) => selectedSet.has(s.id));
        if (toDelete.length > 0) {
            setBulkDeleteSecrets(toDelete);
        }
    }, [selectedKeys, sorted]);

    const selectionMode = canDeleteSecret ? "multiple" : ("none" as const);

    // ── Render ───────────────────────────────────────────────────────

    return (
        <div data-testid="secrets-table">
            <DataTable
                aria-label="Secrets"
                columns={columns}
                data={sorted}
                getRowId={(secret) => secret.id}
                getRowTextValue={(secret) => secret.name}
                selectionMode={selectionMode}
                selectedKeys={selectionMode === "multiple" ? selectedKeys : undefined}
                onSelectionChange={selectionMode === "multiple" ? setSelectedKeys : undefined}
                actionBar={
                    canDeleteSecret
                        ? ({ selectedCount }) => (
                              <>
                                  <span className="text-sm text-content-secondary">
                                      {selectedCount} {pluralize(selectedCount, "secret")} selected
                                  </span>
                                  <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={handleBulkDelete}
                                      data-tracking-id="bulk-delete-secrets-table"
                                  >
                                      Delete
                                  </Button>
                              </>
                          )
                        : undefined
                }
                rowActions={hasRowActions ? rowActions : undefined}
                mobileRow={mobileRow}
            />

            {secretToDelete && <DeleteSecretModal secret={secretToDelete} onClose={() => setSecretToDelete(null)} />}
            {secretToEdit && <EditSecretModal secret={secretToEdit} onClose={() => setSecretToEdit(null)} />}
            {bulkDeleteSecrets && (
                <BulkDeleteSecretsModal
                    secrets={bulkDeleteSecrets}
                    onClose={() => {
                        setBulkDeleteSecrets(null);
                        setSelectedKeys(new Set());
                    }}
                />
            )}
        </div>
    );
};

// ── Helper components ───────────────────────────────────────────────

const CreatedByCell: FC<{ creatorId?: string }> = ({ creatorId }) => {
    const { data: member } = useMemberQuery(creatorId || "", { enabled: !!creatorId });
    return (
        <Text className="truncate text-base">
            {member?.fullName || <span className="text-content-secondary">--</span>}
        </Text>
    );
};

const CreatedByName: FC<{ creatorId?: string }> = ({ creatorId }) => {
    const { data: member } = useMemberQuery(creatorId || "", { enabled: !!creatorId });
    return <>{member?.fullName || <span className="text-content-secondary">--</span>}</>;
};

// ── Bulk delete modal ───────────────────────────────────────────────

const BulkDeleteSecretsModal: FC<{
    secrets: PlainSecret[];
    onClose: () => void;
}> = ({ secrets, onClose }) => {
    const { toast } = useToast();
    const deleteSecret = useDeleteSecret();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setIsDeleting(true);
            const results = await Promise.allSettled(secrets.map((secret) => deleteSecret.mutateAsync({ secret })));
            const failed = results.filter((r) => r.status === "rejected");
            const succeeded = results.length - failed.length;
            if (failed.length === 0) {
                toast({ title: `Deleted ${succeeded} ${pluralize(succeeded, "secret")}` });
            } else if (succeeded === 0) {
                toast({
                    title: "Failed to delete secrets",
                    description: formatError(failed[0].status === "rejected" ? failed[0].reason : undefined),
                });
            } else {
                toast({
                    title: `Deleted ${succeeded} of ${results.length} ${pluralize(results.length, "secret")}`,
                    description: `${failed.length} ${pluralize(failed.length, "deletion")} failed`,
                });
            }
            setIsDeleting(false);
            onClose();
        },
        [secrets, deleteSecret, toast, onClose],
    );

    return (
        <Dialog open onOpenChange={(open: boolean) => !open && onClose()}>
            <DialogContent
                data-testid="bulk-delete-secrets-modal"
                data-track-location={TrackLocations.DeleteSecretModal}
            >
                <DialogHeader>
                    <DialogTitle>
                        Delete {secrets.length} {pluralize(secrets.length, "secret")}
                    </DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <DialogBody>
                    <form id="bulk-delete-secrets-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <Text className="text-base">
                            Are you sure you want to delete {secrets.length} {pluralize(secrets.length, "secret")}?
                        </Text>
                        <div className="flex flex-col gap-1 rounded-xl border-[0.5px] border-border-base bg-surface-glass px-6 py-3">
                            {secrets.map((s) => (
                                <Text key={s.id} className="text-base font-bold">
                                    {s.name}
                                </Text>
                            ))}
                        </div>
                    </form>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="outline" autoFocus={true}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        variant="destructive"
                        form="bulk-delete-secrets-form"
                        loading={isDeleting}
                        disabled={isDeleting}
                        data-testid="bulk-delete-secrets-submit-button"
                        data-tracking-id="confirm-bulk-delete-secrets"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
